import { inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../../features/auth/auth.service';

/**
 * Runs once when the app boots, BEFORE the router activates the first
 * route. This is the piece that was missing: without it, `pageGuard` only
 * ever checked "is there a token string in localStorage", never whether it
 * was still valid — so an expired access token silently piled up broken
 * requests instead of triggering a clean silent-refresh-or-login-redirect
 * before the page even rendered.
 *
 * Flow (delegated to AuthService.initializeAuth()):
 *   1. No token/session at all        → resolves false, user sees /login.
 *   2. Access token still valid       → restores session instantly.
 *   3. Access token expired but a
 *      session id is on record        → silently calls /auth/refresh-token.
 *      - succeeds → new token stored, session restored, no re-login.
 *      - fails    → backend session (the 1-week window) is truly gone,
 *                    auth data is cleared, user is sent to /login.
 */ 
export function initializeAppAuth(): Promise<boolean> {
  const authService = inject(AuthService);
  return firstValueFrom(authService.initializeAuth());
}